import { useCallback, useDebugValue, useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { getProfileFlowStepsConfig } from '../components/profile/modalFlow/stepsConfig';
import { commonApplicationProfileAtom } from '../state/atoms/commonApplicationProfile';
import { profileCompletionFlowStepIndex } from '../state/atoms/profileCompletionFlowStepIndex';
import { EnrollmentStatus } from '../types/commonApplicationProfile';
import { ProfileCompletionFlowState, ProfileCompletionFlowStep } from '../types/profileCompletionFlow';
import { ProfileCompletionStepDefinition } from '../types/ProfileCompletionStep';
import { StudentSponsorshipApplication } from '../types/sponsorshipApplication';

/**
 * Hook to control the flow of the profile completion modal process.
 * @returns {ProfileCompletionFlowState} State of the current flow and methods to update the flow state
 */

export const useProfileCompletionFlow = (sponsorshipApplication: StudentSponsorshipApplication): ProfileCompletionFlowState => {
  // Recoil state to store the current step index
  const [ currentStepIndex, setCurrentStepIndex ] = useRecoilState(profileCompletionFlowStepIndex);
  useDebugValue(currentStepIndex);

  // Get the enrollment status
  const commonApplicationProfile = useRecoilValue(commonApplicationProfileAtom);
  const isEnrolled = useMemo(() => commonApplicationProfile?.enrollment_status === EnrollmentStatus.ENROLLED, [commonApplicationProfile?.enrollment_status]);

  // Memoized steps config
  const stepsConfig = useMemo(
    () => getProfileFlowStepsConfig(sponsorshipApplication, isEnrolled),
    [ sponsorshipApplication, isEnrolled ],
  );
  const stepsList = Object.values(stepsConfig);
  const [ currentStep, setCurrentStep ] = useState<ProfileCompletionStepDefinition | null>(stepsList?.[currentStepIndex] ?? null);
  useDebugValue(currentStep);

  const reset = useCallback(() => {
    setCurrentStepIndex(0);
    setCurrentStep(stepsList[0]);
  }, [ setCurrentStep, stepsList, setCurrentStepIndex ]);

  // Reset the flow if the current step is not set
  useEffect(() => {
    if (!currentStep && stepsList.length > 0) reset();
  }, [ currentStep, stepsList, reset ]);

  const getStep = useCallback((step: ProfileCompletionFlowStep) => stepsConfig[step], [stepsConfig]);

  const navigateNext = useCallback(() => {
    if (currentStepIndex === stepsList.length - 1) return;
    const nextIndex = currentStepIndex + 1;

    setCurrentStepIndex(nextIndex);
    setCurrentStep(stepsList[nextIndex]);
  }, [ stepsList, currentStepIndex, setCurrentStep, setCurrentStepIndex ]);

  const navigatePrevious = useCallback(() => {
    if (currentStepIndex === 0) return;
    const prevIndex = currentStepIndex - 1;

    setCurrentStepIndex(prevIndex);
    setCurrentStep(stepsList[prevIndex]);
  }, [ stepsList, currentStepIndex, setCurrentStep, setCurrentStepIndex ]);

  const navigateTo = useCallback((step: ProfileCompletionFlowStep) => {
    if (!Object.keys(stepsConfig).includes(step)) {
      throw Error(`Unknown step ${step}. Please use the following steps: ${Object.keys(stepsConfig).join(', ')}`);
    }
    const stepObj = getStep(step);

    setCurrentStepIndex(stepsList.indexOf(stepObj));
    setCurrentStep(stepObj);
  }, [ setCurrentStep, stepsConfig, stepsList, setCurrentStepIndex, getStep ]);

  return {
    currentStep,
    getStep,
    navigateNext,
    navigatePrevious,
    navigateTo,
    reset,
    stepsList,
  };
};
